import { computed, unref } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { campaignStatus } from '@/utils/domain/promo/promoStatus';
import { campaignGauges } from '@/utils/domain/promo/promoValues';
import { PHASE, campaignPhase, campaignPhaseKey, isNegoPhase, offerPhase } from '@/utils/domain/promo/promoPhase';
import { getForecastPeriod, isInForecastPeriod } from '@/utils/domain/promo/promoForecast';
import { getOffer } from '@/utils/domain/promo/promoModel';

// What the campaign views of the promo step (#23521) share : the desktop panel, the mobile list card and the mobile
// campaign page all show the same status, phase, period and offers.

// The means association is hidden until its own ticket (spec §4 : a dedicated specification) ; true shows the
// "Associer des moyens" (desktop) and "Moyens" (mobile) buttons again.
const MEANS_VISIBLE = false;

export function usePromoCampaign(campaignSource) {
	const { snapshot, format, t } = usePromoStep();
	const campaign = computed(() => unref(campaignSource));

	const status = computed(() => campaignStatus(snapshot.value, campaign.value));
	const gauges = computed(() => campaignGauges(snapshot.value, campaign.value));
	const phase = computed(() => offerPhase(snapshot.value, campaign.value));
	const forecastPeriod = computed(() => getForecastPeriod(campaign.value));
	const inForecastPeriod = computed(() => isInForecastPeriod(campaign.value, snapshot.value.now));
	const showForecastPeriod = computed(() => !campaign.value.promoLocal && !!forecastPeriod.value.start);
	const meansEditable = computed(() => !snapshot.value.readonly && isNegoPhase(phase.value));
	const meansVisible = computed(() => MEANS_VISIBLE && campaign.value.applicationDomains.length > 0);

	const periodText = (start, end) => `${format.date(start) || t('N/A')} → ${format.date(end) || t('N/A')}`;

	const subtitle = computed(() => {
		const key = campaignPhaseKey(snapshot.value, campaign.value);
		if (!key) return '';
		return t(inForecastPeriod.value ? 'PromoPhaseForecastInProgress' : key);
	});

	const periodChip = computed(() => {
		const dates = campaign.value.dates;
		const shown = campaignPhase(snapshot.value, campaign.value);
		if (inForecastPeriod.value) return `${t('PromoForecastPeriod')} · ${periodText(forecastPeriod.value.start, forecastPeriod.value.end)}`;
		if (shown === PHASE.STATEMENT) return `${t('CampaignStorePeriod')} · ${periodText(dates.storeBegin, dates.storeEnd)}`;
		if (shown === PHASE.PENDING) return t('CampaignFixNegoPeriod');
		if (shown === PHASE.NEGO) return `${t('CampaignNegoPeriod')} · ${periodText(dates.negoStart, dates.negoEnd)}`;
		return '';
	});

	const offers = computed(() => {
		if (campaign.value.promoLocal) {
			return campaign.value.localOfferKeys
				.map((key) => {
					const offerInfo = snapshot.value.model.offerInfos.find((oi) => oi._key === key);
					const offer = offerInfo ? getOffer(campaign.value, offerInfo.campaignOfferId) : null;
					return offer ? { key, offer, offerKey: key } : null;
				})
				.filter(Boolean);
		}
		return campaign.value.offers.filter((offer) => offer.visible && offer.hasVisibleProducts).map((offer) => ({ key: String(offer.id), offer, offerKey: null }));
	});

	const tiles = computed(() => {
		const c = campaign.value;
		const list = [{ key: 'description', icon: 'notes', label: t('Description'), display: c.description || t('N/A'), wide: true, muted: !c.description }];
		if (showForecastPeriod.value) list.push({ key: 'forecast', icon: 'online_prediction', label: t('PromoForecastPeriod'), display: periodText(forecastPeriod.value.start, forecastPeriod.value.end) });
		list.push({ key: 'nego', icon: 'handshake', label: t('CampaignNegoPeriod'), display: periodText(c.dates.negoStart, c.dates.negoEnd) });
		list.push({ key: 'store', icon: 'storefront', label: t('CampaignStorePeriod'), display: periodText(c.dates.storeBegin, c.dates.storeEnd) });
		list.push({ key: 'eventTag', icon: 'celebration', label: t('EventTag'), display: c.eventTags || t('N/A'), muted: !c.eventTags });
		list.push({ key: 'support', icon: 'newspaper', label: t('PromoSupport'), display: c.prospectus ? t('PromoSupportProspectus') : t('N/A'), muted: !c.prospectus });
		list.push({ key: 'points', icon: 'emoji_events', label: t('Scoring'), display: c.pointsNegotiationActive || c.pointsStatementActive ? t('Yes') : t('No') });
		if (c.promoLocal) list.push({ key: 'offers', icon: 'local_offer', label: t('Offer'), display: String(c.localOfferKeys.length) });
		if (c.documents.length) list.push({ key: 'documents', icon: 'attach_file', label: t('Documents'), display: '', wide: true });
		return list;
	});

	return { campaign, status, gauges, phase, subtitle, periodChip, offers, tiles, meansEditable, meansVisible };
}
